import { createFileRoute, Link } from "@tanstack/react-router";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import dopo1 from "@/assets/dopo1.webp.asset.json";
import {
  ANNI_ESPERIENZA,
  ANNO_FONDAZIONE,
  EMAIL,
  GOOGLE_MAPS_URL,
  GOOGLE_REVIEW_URL,
  TEL_ANTONIO,
  TEL_LUCA,
  WHATSAPP_URL,
  province,
} from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/contattaci";
const TITOLO = "Contatti | Artigiani del Cotto – Preventivo gratuito Lombardia";
const DESCRIZIONE =
  "Contattaci per lavaggio e trattamento di pavimenti in cotto, marmi e grès in tutta la Lombardia. Telefono 339 751 7813, WhatsApp o email: sopralluogo e preventivo gratuiti.";
const IMMAGINE = `https://artigianidelcotto.com${dopo1.url}`;

const contatti = [
  { label: "Antonio", valore: "+39 339 751 7813", href: `tel:${TEL_ANTONIO}` },
  { label: "Luca", valore: "+39 351 720 4660", href: `tel:${TEL_LUCA}` },
  { label: "Email", valore: EMAIL, href: `mailto:${EMAIL}` },
];

export const Route = createFileRoute("/contattaci")({
  component: Contattaci,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: IMMAGINE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMMAGINE },
      { name: "geo.region", content: "IT-25" },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              url: "https://artigianidelcotto.com/",
              telephone: "+39 339 751 7813",
              email: EMAIL,
              image: IMMAGINE,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              hasMap: GOOGLE_MAPS_URL,
              address: {
                "@type": "PostalAddress",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  telephone: "+39 339 751 7813",
                  email: EMAIL,
                  areaServed: "IT-25",
                  availableLanguage: "Italian",
                },
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  telephone: "+39 351 720 4660",
                  areaServed: "IT-25",
                  availableLanguage: "Italian",
                },
              ],
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
            },
            {
              "@type": "ContactPage",
              "@id": `${URL_PAGINA}#pagina`,
              url: URL_PAGINA,
              name: TITOLO,
              description: DESCRIZIONE,
              inLanguage: "it-IT",
              primaryImageOfPage: { "@type": "ImageObject", url: IMMAGINE },
              isPartOf: {
                "@type": "WebSite",
                "@id": "https://artigianidelcotto.com/#sito",
                url: "https://artigianidelcotto.com/",
                name: "Artigiani del Cotto",
              },
              about: { "@id": "https://artigianidelcotto.com/#azienda" },
              breadcrumb: { "@id": `${URL_PAGINA}#briciole` },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Contatti", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function Contattaci() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Contatti</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Contattaci: sopralluogo e preventivo gratuiti
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Chiamaci, scrivici su WhatsApp o mandaci due foto del pavimento: ti diamo una prima stima in
              giornata. {ANNI_ESPERIENZA} di esperienza su cotto, marmi, graniglie e grès.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Chiama 339 751 7813
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Scrivici su WhatsApp
              </a>
            </div>
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Come raggiungerci</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">Rispondiamo noi, non un centralino.</h2>
              <p className="mt-4 text-muted-foreground">
                Siamo una piccola impresa artigiana con sede a Torbole Casaglia (Brescia) e lavoriamo su tutta la
                Lombardia. Il sopralluogo e il preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                {contatti.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                  >
                    <span className="font-medium">{c.label}</span>
                    <span className="text-primary">{c.valore}</span>
                  </a>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
              <dl className="mt-10 grid gap-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-semibold">Orari</dt>
                  <dd className="mt-1 text-sm text-muted-foreground">
                    Lunedì – sabato, 8:00 – 19:00. Se non rispondiamo siamo in cantiere: lascia un messaggio,
                    richiamiamo noi.
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-semibold">Dove siamo</dt>
                  <dd className="mt-1 text-sm text-muted-foreground">
                    Torbole Casaglia (BS) — non abbiamo showroom, lavoriamo direttamente a casa del cliente.{" "}
                    <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
                      Scheda Google
                    </a>{" "}
                    ·{" "}
                    <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
                      Lascia una recensione
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
            <PreventivoForm />
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Zone servite</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">Interveniamo in tutta la Lombardia.</h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Dalle grandi città ai paesi più piccoli: raggiungiamo tutte le province lombarde senza costi di
              trasferta sul sopralluogo.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => (
                <li key={p} className="rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90">
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Prima di chiamare</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">Dai un'occhiata ai servizi.</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              { to: "/pulizia-lavaggio-cotto", titolo: "Pulizia e lavaggio del cotto", testo: "Lavaggio profondo, deceratura e protezione antimacchia." },
              { to: "/trattamento-cotto-esterno", titolo: "Trattamento cotto esterno", testo: "Porticati, terrazze e cortili: protettivi traspiranti antigelo." },
              { to: "/come-pulire-il-cotto", titolo: "Come pulire il cotto", testo: "La guida pratica alla manutenzione quotidiana del cotto." },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="surface-warm rounded-sm border border-border p-6 transition-transform hover:-translate-y-1"
              >
                <h3 className="text-lg font-semibold">{c.titolo}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.testo}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
